// HKDSE 2023 Paper 1 Knowledge Graph Samples - Complete Set
// Organized by question types with systematic naming

export const hkdse2023Samples_complete = {
  algebra: [
    {
      id: 'hkdse2023_algebra_01',
      problem: "令 $h$ 成為公式 $\\frac{5}{h+k} = \\frac{k}{h-3}$ 的主項。",
      problemType: "algebra_formula",
      difficulty: "intermediate",
      description: "Rearranging formula to make h the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fractions on both sides",
            value: "5/(h+k) = k/(h-3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "methods",
            description: "Multiplying both sides to eliminate fractions",
            value: "5(h-3) = k(h+k)",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "subject_formula",
            name: "Subject of Formula",
            type: "concepts",
            description: "Making h the subject of the formula",
            value: "h = ...",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "variable_h",
            name: "Variable h",
            type: "variables",
            description: "Variable to be made the subject",
            value: "h",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "fraction_equation",
            to: "cross_multiplication",
            type: "solved_by",
            strength: 9
          },
          {
            from: "cross_multiplication",
            to: "subject_formula",
            type: "leads_to",
            strength: 9
          },
          {
            from: "subject_formula",
            to: "variable_h",
            type: "expresses",
            strength: 8
          }
        ]
      }
    },
    {
      id: 'hkdse2023_algebra_02',
      problem: "化簡 $\\frac{x^{-8}y}{(x^7y^9)^{-6}}$，並以正指數表示答案。",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_quotient",
            name: "Power of Quotient",
            type: "concepts",
            description: "(a/b)ⁿ = aⁿ/bⁿ",
            value: "(x⁷y⁹)⁻⁶ = x⁻⁴²y⁻⁵⁴",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "methods",
            description: "aᵐ/aⁿ = aᵐ⁻ⁿ",
            value: "x⁻⁸y/x⁻⁴²y⁻⁵⁴ = x³⁴y⁵⁵",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "positive_index_form",
            name: "Positive Index Form",
            type: "concepts",
            description: "Converting to positive index form",
            value: "x³⁴y⁵⁵",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "negative_indices",
            to: "power_of_quotient",
            type: "applied_to",
            strength: 9
          },
          {
            from: "power_of_quotient",
            to: "division_of_powers",
            type: "enables",
            strength: 9
          },
          {
            from: "division_of_powers",
            to: "positive_index_form",
            type: "results_in",
            strength: 8
          }
        ]
      }
    }
  ],
  rounding: [
    {
      id: 'hkdse2023_rounding_01',
      problem: "若一包芝士的重量量得 $220$ g 準確至最接近的 $10$ g，則稱它為普通裝。某人宣稱 $250$ 包普通裝芝士的總重量可量得 $53.6$ kg 準確至最接近的 $0.1$ kg。該宣稱是否正確？試解釋你的答案。",
      problemType: "rounding_accuracy",
      difficulty: "intermediate",
      description: "Analyzing accuracy in measurements and calculations",
      graphData: {
        nodes: [
          {
            id: "individual_accuracy",
            name: "Individual Accuracy",
            type: "concepts",
            description: "Accuracy of individual cheese package",
            value: "220g ± 5g (nearest 10g)",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "total_weight",
            name: "Total Weight",
            type: "concepts",
            description: "Total weight of 250 packages",
            value: "250 × 220g = 55kg",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "claimed_accuracy",
            name: "Claimed Accuracy",
            type: "concepts",
            description: "Claimed accuracy of total weight",
            value: "53.6kg ± 0.05kg (nearest 0.1kg)",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "accuracy_analysis",
            name: "Accuracy Analysis",
            type: "methods",
            description: "Analyzing if claim is correct",
            value: "Compare actual range with claimed range",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "individual_accuracy",
            to: "total_weight",
            type: "affects",
            strength: 9
          },
          {
            from: "total_weight",
            to: "claimed_accuracy",
            type: "compared_with",
            strength: 8
          },
          {
            from: "claimed_accuracy",
            to: "accuracy_analysis",
            type: "evaluated_by",
            strength: 8
          }
        ]
      }
    }
  ],
  inequalities: [
    {
      id: 'hkdse2023_inequalities_01',
      problem: "考慮複合不等式 $3x + 2 > \\frac{4x-5}{2}$ 及 $3x - 2 < 7$ ............(*)。(a) 解 (*)。(b) 有多少個負整數滿足 (*)？",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities with 'and'",
      graphData: {
        nodes: [
          {
            id: "first_inequality",
            name: "First Inequality",
            type: "concepts",
            description: "3x + 2 > (4x-5)/2",
            value: "3x + 2 > (4x-5)/2",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "second_inequality",
            name: "Second Inequality",
            type: "concepts",
            description: "3x - 2 < 7",
            value: "3x - 2 < 7",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "compound_inequality",
            name: "Compound Inequality",
            type: "concepts",
            description: "Inequality with 'and' condition",
            value: "First AND Second",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "negative_integers",
            name: "Negative Integers",
            type: "concepts",
            description: "Counting negative integers in solution set",
            value: "Count negative integers satisfying (*)",
            importance: 7,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "first_inequality",
            to: "compound_inequality",
            type: "part_of",
            strength: 9
          },
          {
            from: "second_inequality",
            to: "compound_inequality",
            type: "part_of",
            strength: 8
          },
          {
            from: "compound_inequality",
            to: "negative_integers",
            type: "constrains",
            strength: 7
          }
        ]
      }
    }
  ],
  word_problems: [
    {
      id: 'hkdse2023_word_problems_01',
      problem: "在某渡輪上，女乘客人數較男乘客人數多 $40\\%$。若 $24$ 名女乘客離開該渡輪，則男乘客人數較女乘客人數多 $40\\%$。求在該渡輪上男乘客人數。",
      problemType: "word_problems_percentage",
      difficulty: "intermediate",
      description: "Solving percentage problems with changing quantities",
      graphData: {
        nodes: [
          {
            id: "initial_ratio",
            name: "Initial Ratio",
            type: "concepts",
            description: "Initial ratio of female to male passengers",
            value: "Females = Males × 1.4",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "after_change",
            name: "After Change",
            type: "concepts",
            description: "Ratio after 24 females leave",
            value: "Males = (Females-24) × 1.4",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "system_equations",
            name: "System of Equations",
            type: "methods",
            description: "Setting up equations to solve",
            value: "F = 1.4M, M = 1.4(F-24)",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "male_passengers",
            name: "Male Passengers",
            type: "variables",
            description: "Number of male passengers to find",
            value: "M",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "initial_ratio",
            to: "system_equations",
            type: "provides_equation",
            strength: 9
          },
          {
            from: "after_change",
            to: "system_equations",
            type: "provides_equation",
            strength: 9
          },
          {
            from: "system_equations",
            to: "male_passengers",
            type: "solves_for",
            strength: 10
          }
        ]
      }
    }
  ],
  ratios: [
    {
      id: 'hkdse2023_ratios_01',
      problem: "設 $a$、$b$ 及 $c$ 均為非零的數使得 $7a = 6b$ 及 $\\frac{4a-3c}{2b-c} = 9$。求 (a) $a:b:c$，(b) $\\frac{5a+8b}{7b+3c}$。",
      problemType: "ratios_proportion",
      difficulty: "intermediate",
      description: "Working with ratios and proportions",
      graphData: {
        nodes: [
          {
            id: "ratio_ab",
            name: "Ratio a:b",
            type: "concepts",
            description: "Ratio of a to b",
            value: "7a = 6b",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "proportion_equation",
            name: "Proportion Equation",
            type: "concepts",
            description: "Proportion involving a, b, c",
            value: "(4a-3c)/(2b-c) = 9",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "variable_relationships",
            name: "Variable Relationships",
            type: "methods",
            description: "Expressing variables in terms of one variable",
            value: "a = 6k, b = 7k, c = ...",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "target_expression",
            name: "Target Expression",
            type: "concepts",
            description: "Expression to be evaluated",
            value: "(5a+8b)/(7b+3c)",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "ratio_ab",
            to: "variable_relationships",
            type: "provides_constraint",
            strength: 9
          },
          {
            from: "proportion_equation",
            to: "variable_relationships",
            type: "provides_constraint",
            strength: 9
          },
          {
            from: "variable_relationships",
            to: "target_expression",
            type: "evaluates",
            strength: 8
          }
        ]
      }
    }
  ],
  geometry: [
    {
      id: 'hkdse2023_geometry_01',
      problem: "圖1中，$PR$ 為圓 $PQRS$ 的一直徑。將 $PR$ 與 $QS$ 的交點記為 $T$。Figure 1 description: A circle with points P, Q, R, S on the circumference. PR is a diameter of the circle. QS is a chord that intersects PR at point T inside the circle. 若 $\\angle PSQ = 41°$ 及 $\\angle PTQ = 68°$，求 $\\angle RQS$ 及 $\\angle PQS$。",
      problemType: "geometry_circle_theorems",
      difficulty: "intermediate",
      description: "Working with circle theorems and angles",
      graphData: {
        nodes: [
          {
            id: "circle_theorems",
            name: "Circle Theorems",
            type: "concepts",
            description: "Angles in circles and cyclic quadrilaterals",
            value: "Angle properties in circles",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "diameter_property",
            name: "Diameter Property",
            type: "concepts",
            description: "PR is diameter, angle in semicircle = 90°",
            value: "Angle in semicircle = 90°",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "given_angles",
            name: "Given Angles",
            type: "variables",
            description: "Known angle measurements",
            value: "∠PSQ = 41°, ∠PTQ = 68°",
            importance: 8,
            x: 200, y: 0, z: 0
          },
          {
            id: "target_angles",
            name: "Target Angles",
            type: "variables",
            description: "Angles to be found",
            value: "∠RQS, ∠PQS",
            importance: 9,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "circle_theorems",
            to: "diameter_property",
            type: "includes",
            strength: 9
          },
          {
            from: "given_angles",
            to: "target_angles",
            type: "constrains",
            strength: 8
          },
          {
            from: "diameter_property",
            to: "target_angles",
            type: "affects",
            strength: 9
          }
        ]
      }
    }
  ]
}; 